import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import {
  Checked,
  Connection,
  CollectionTag,
  Cpu,
  Document,
  Download,
  Key,
  Lightning,
  Link,
  List,
  Odometer,
  OfficeBuilding,
  Postcard,
  Suitcase,
  Tickets,
  Unlock,
  Upload,
  UploadFilled,
  Van,
  UserFilled
} from '@element-plus/icons-vue'

/**
 * Vocabulary note: the per-unit records are "acceptance records", not "test records". This is Lime
 * accepting a delivery from a supplier, and "test" is both vague on a factory floor and easily read as
 * software testing. It also pairs with "acceptance standards" — the standard says what counts as
 * acceptable, the record says whether this unit met it.
 *
 * The navigation tree, and the only place a page's route, label, icon and required role are declared
 * together.
 *
 * The router builds its routes from this and the sidebar renders from it, so a page cannot end up
 * reachable but unlisted, or listed but denied. Getting that wrong in either direction is how an
 * operator either sees a menu item that 403s or finds a page nobody meant to expose.
 *
 * **Organised by the thing a page is about — a vehicle, a CCU — and not by the station that handles it.** It used to be by station, which meant a section per station and therefore the same three
 * kinds of page (records, check items, standards) repeated in each: the number of headings grew with the
 * number of stations, and flashing arrived as a heading with one item under it. A handful of groups is what a
 * person can hold in their head, and few enough that collapsing them keeps the sidebar off the scrollbar —
 * see `DefaultLayout`, which opens only the group holding the current page.
 *
 * A group is not a URL prefix, deliberately. Paths stay grouped by bounded context and station
 * (`ccu-eol/`, `provision/`, `product/`), because a link that has been sent to somebody must keep working
 * and the schema underneath is grouped that way too. Grouping here is what the sidebar shows, nothing more.
 */

/**
 * Permissions as issued by the backend, resolved there from the operator's role.
 *
 * Pages are gated on a permission and never on a role name: a role is a bundle that an administrator can
 * change, so `POLICY_ADMIN` in a component would be a guess about what that bundle currently contains.
 * These strings must match rows in `iam_role_permission`.
 */
export const Permission = {
  AcceptanceStandardRead: 'acceptance:standard:read',
  AcceptanceStandardWrite: 'acceptance:standard:write',
  ProductModelRead: 'product:model:read',
  ProductModelWrite: 'product:model:write',
  ProductSkuRead: 'product:sku:read',
  ProductSkuWrite: 'product:sku:write',
  EolRecordRead: 'eol:record:read',
  BindingVehicleRead: 'binding:vehicle:read',
  BindingVehicleConsole: 'binding:vehicle:console',
  IdentityCcuRead: 'identity:ccu:read',
  IdentityCcuAdmit: 'identity:ccu:admit',
  ProvisionCcuRead: 'provision:ccu:read',
  StockCodeRead: 'stock:code:read',
  StockCodeIssue: 'stock:code:issue',
  ShipmentCcuCheck: 'shipment:ccu:check',
  ShipmentCcuRead: 'shipment:ccu:read',
  LegacyCodeRead: 'legacy:code:read',
  ToolReleaseRead: 'tool:release:read',
  ToolReleaseWrite: 'tool:release:write',
  FirmwareReleaseRead: 'firmware:release:read',
  FirmwareReleaseWrite: 'firmware:release:write',
  FirmwareCcu5Download: 'firmware:ccu5:download',
  // One per SOP, and none gates a page: each decides which accounts are shown that document — see
  // src/help/sops.ts. Their own permissions rather than the bench's work permission, so who reads an instruction
  // and who may do the work are granted separately.
  SopCcuOutgoing: 'sop:ccu:outgoing',
  SopCcuIncoming: 'sop:ccu:incoming',
  SopVehicleBinding: 'sop:vehicle:binding',
  SopVehicleStatic: 'sop:vehicle:static',
  SopVehicleRide: 'sop:vehicle:ride',
  SopShipmentCcu: 'sop:shipment:ccu',
  IamOperatorRead: 'iam:operator:read',
  IamOperatorWrite: 'iam:operator:write',
  IamOrganisationRead: 'iam:organisation:read',
  IamOrganisationWrite: 'iam:organisation:write',
  IamRoleRead: 'iam:role:read',
  IamRoleWrite: 'iam:role:write',
  // Gates the two access review downloads on the operators and roles pages, not a page of its own.
  IamAuditExport: 'iam:audit:export',
  // Gates buttons rather than a page, like BindingVehicleConsole: the accepted-module list is reached with
  // IdentityCcuRead, and this decides whether the commands on a row can be sent.
  OpsVehicleCommand: 'ops:vehicle:command',
  OpsVehicleAdvanced: 'ops:vehicle:advanced',
  OpsVehicleRead: 'ops:vehicle:read',
  OpsChirpMint: 'ops:chirp:mint'
} as const

export type PermissionName = (typeof Permission)[keyof typeof Permission]

/**
 * Held by the ADMINISTRATOR role, and the only wildcard there is: it means every permission, including
 * ones added after the role was defined. Anything more expressive — `acceptance:*`, `*:read` — would mean
 * a page's requirement could no longer be checked by comparing strings, and "what can this account do"
 * would stop being answerable by looking at a list.
 */
export const ALL_PERMISSIONS = '*'

export interface NavItem {
  /** Route name; also the key used everywhere else. */
  name: string
  /** Path relative to the layout route. */
  path: string
  /** i18n key for the label. */
  labelKey: string
  /** Drawn before the label. Optional: an entry may be the label alone, as SOPs is. */
  icon?: Component
  /** Lazy-loaded view. Typed from vue-router so the router can consume it without a cast. */
  component: NonNullable<RouteRecordRaw['component']>
  /**
   * Permissions that allow reaching it. Empty means any signed-in operator.
   * A user without one of these neither sees the item nor can navigate to it directly.
   */
  permissions?: PermissionName[]
  /**
   * Routed but never listed in the menu: a page reached only by a link someone was sent, which has nothing to show
   * when opened from the menu. Its section disappears for an account that has nothing else in it.
   */
  hidden?: boolean
}

export interface NavSection {
  /** i18n key for the section heading, or null for the ungrouped items at the top. */
  labelKey: string | null
  items: NavItem[]
}

export const navigation: NavSection[] = [
  {
    labelKey: null,
    items: [
      {
        name: 'dashboard',
        path: 'dashboard',
        labelKey: 'nav.dashboard',
        icon: Odometer,
        component: () => import('@/views/DashboardView.vue')
      },
      {
        // Open to every signed-in operator: which documents are listed is decided per document (src/help/sops.ts),
        // and an account with none is told so. The path keeps its help/ prefix because links to it have already
        // been sent.
        name: 'sops',
        path: 'help/sops',
        labelKey: 'nav.sops',
        component: () => import('@/views/help/SopListView.vue')
      },
      {
        // Ungated here and gated by the page itself, because the permission depends on which document the
        // address names and a route can only declare one list. Hidden: it has nothing to show without a code.
        name: 'sop',
        path: 'help/sops/:code',
        labelKey: 'nav.sop',
        hidden: true,
        component: () => import('@/views/help/SopView.vue')
      }
    ]
  },
  {
    // Everything about a whole vehicle: what it is, what it is judged by, and what happened to one. In that
    // order — each item is defined in terms of the one above it.
    labelKey: 'nav.section.vehicle',
    items: [
      {
        // First, and before the SKU: a SKU is a build of a model and cannot be created without one, a
        // standard is written for a SKU, and a record is judged against that standard — so this is where a
        // reader starts. Its own permission pair too, because this page carries the frame-number positions
        // stamped on hardware, which is a narrower thing to be allowed to change than a bill of materials.
        name: 'vehicle-models',
        path: 'product/models',
        labelKey: 'nav.vehicleModels',
        icon: Van,
        component: () => import('@/views/product/VehicleModelListView.vue'),
        permissions: [Permission.ProductModelRead]
      },
      {
        // Not the acceptance permission: what a model is made of and what counts as an acceptable version
        // of it are different decisions, maintained on different clocks.
        name: 'skus',
        path: 'product/skus',
        labelKey: 'nav.skus',
        icon: Suitcase,
        component: () => import('@/views/product/SkuListView.vue'),
        permissions: [Permission.ProductSkuRead]
      },
      {
        name: 'vehicle-acceptance-records',
        path: 'vehicle-eol/records',
        labelKey: 'nav.acceptanceRecords',
        icon: Tickets,
        component: () => import('@/views/vehicle-eol/AcceptanceRecordListView.vue'),
        permissions: [Permission.EolRecordRead]
      },
      {
        // A binding is what every later step addresses a vehicle through, so the people reading it are the
        // people reading vehicle records.
        name: 'vehicle-bindings',
        path: 'vehicle-eol/bindings',
        labelKey: 'nav.vehicleBindings',
        icon: Link,
        component: () => import('@/views/vehicle-eol/BindingListView.vue'),
        permissions: [Permission.BindingVehicleRead]
      },
      {
        // After bindings, because a command is addressed through one — and the rows with no binding on them
        // are the exception this page exists to make visible rather than the rule.
        name: 'vehicle-commands',
        path: 'vehicle-eol/commands',
        labelKey: 'nav.vehicleCommands',
        icon: Unlock,
        component: () => import('@/views/vehicle-eol/VehicleCommandListView.vue'),
        // Its own permission, and not the one that sends a command: a bench files what it did at that bench,
        // and reading every command at every factory is a console question.
        permissions: [Permission.OpsVehicleRead]
      },
      {
        // The catalogue of what can be checked, before the standards that pick from it: you read what
        // exists before you decide what to require.
        name: 'vehicle-check-items',
        path: 'vehicle-eol/check-items',
        labelKey: 'nav.checkItems',
        icon: List,
        component: () => import('@/views/vehicle-eol/CheckItemListView.vue'),
        permissions: [Permission.AcceptanceStandardRead]
      },
      {
        name: 'vehicle-standards',
        path: 'vehicle-eol/standards',
        labelKey: 'nav.standards',
        icon: Checked,
        component: () => import('@/views/vehicle-eol/StandardListView.vue'),
        permissions: [Permission.AcceptanceStandardRead]
      }
    ]
  },
  {
    // The CCU module, from a bare board to an accepted one. Flashing first because that is the order a
    // module goes through: a board in USB DFU has no operating system and no IMEI, and only once it is
    // flashed is there something a station can accept.
    labelKey: 'nav.section.ccu',
    items: [
      {
        name: 'provision-flash-records',
        path: 'provision/flash-records',
        labelKey: 'nav.flashRecords',
        icon: Lightning,
        component: () => import('@/views/provision/FlashRecordListView.vue'),
        // Not the flash permission the station holds: filing what this bench did and browsing what every
        // bench did are different questions, and only the second is a console one.
        permissions: [Permission.ProvisionCcuRead]
      },
      {
        // An attempt names the account that passed a module, and a supplier station account has no business
        // reading every other station's results.
        name: 'ccu-acceptance-records',
        path: 'ccu-eol/records',
        labelKey: 'nav.acceptanceRecords',
        icon: Tickets,
        component: () => import('@/views/ccu-eol/AcceptanceRecordListView.vue'),
        permissions: [Permission.EolRecordRead]
      },
      {
        // Read-only: an item exists because the code knows how to read it, so it arrives by migration.
        name: 'ccu-check-items',
        path: 'ccu-eol/check-items',
        labelKey: 'nav.checkItems',
        icon: List,
        component: () => import('@/views/ccu-eol/CheckItemListView.vue'),
        permissions: [Permission.AcceptanceStandardRead]
      },
      {
        name: 'ccu-standards',
        path: 'ccu-eol/standards',
        labelKey: 'nav.standards',
        icon: Checked,
        component: () => import('@/views/ccu-eol/StandardListView.vue'),
        // Limits decide whether a unit ships, so they are not an operator's business.
        permissions: [Permission.AcceptanceStandardRead]
      },
      {
        // The cloud identity: the public key and getting it registered. Its own permission, and the same one
        // the endpoint behind it requires — whoever reviews yield has no need of a key.
        name: 'ccu-identity',
        path: 'ccu-eol/identity',
        labelKey: 'nav.ccuIdentity',
        icon: Cpu,
        component: () => import('@/views/ccu-eol/CcuIdentityListView.vue'),
        permissions: [Permission.IdentityCcuRead]
      },
      {
        // After the identities page and gated the same way, because the two are halves of one answer: that
        // one is the cloud key a module proves itself with, this one is what the outside world calls it by
        // and whether the outside world has been told.
        name: 'ccu-devices',
        path: 'ccu-eol/devices',
        labelKey: 'nav.ccuDevices',
        icon: Connection,
        component: () => import('@/views/ccu-eol/CcuDeviceListView.vue'),
        permissions: [Permission.IdentityCcuRead]
      },
      {
        // The supplier's door: upload the list about to ship, see what is not clear. It answers for the list
        // just sent and shows nothing of any earlier one — that is the next item, and Lime's.
        name: 'ccu-shipment-check',
        path: 'ccu-eol/shipment-check',
        labelKey: 'nav.shipmentCheck',
        icon: Upload,
        component: () => import('@/views/ccu-eol/ShipmentCheckView.vue'),
        permissions: [Permission.ShipmentCcuCheck]
      },
      {
        // Every list any supplier has sent, line by line. Its own permission, held by Lime and not by the
        // supplier whose claims it records.
        name: 'ccu-shipment-manifests',
        path: 'ccu-eol/shipment-manifests',
        labelKey: 'nav.shipmentManifests',
        icon: Document,
        component: () => import('@/views/ccu-eol/ShipmentManifestListView.vue'),
        permissions: [Permission.ShipmentCcuRead]
      }
    ]
  },
  {
    labelKey: 'nav.section.tools',
    items: [
      {
        // First, and ungated. The endpoint behind it answers only about the caller — the tools this account
        // may install — so there is no permission to name here that every signed-in operator does not
        // already have. An account with no tool permission sees an empty page that says so.
        name: 'tool-downloads',
        path: 'tools/downloads',
        labelKey: 'nav.toolDownloads',
        icon: Download,
        component: () => import('@/views/tools/ToolDownloadListView.vue')
      },
      {
        name: 'tool-releases',
        path: 'tools/releases',
        labelKey: 'nav.toolReleases',
        icon: UploadFilled,
        component: () => import('@/views/tools/ToolReleaseListView.vue'),
        // Both, because isAllowed is an OR and the endpoints behind the page accept either: whoever may
        // publish a build has to be able to read what is already published, and whoever only reviews what a
        // factory is running must not need the permission to change it.
        permissions: [Permission.ToolReleaseRead, Permission.ToolReleaseWrite]
      }
    ]
  },
  {
    // Its own group rather than under station software: what is downloaded here is written onto a part, not
    // installed on a bench, and the people it is sent to are suppliers following a link in an email.
    labelKey: 'nav.section.firmware',
    items: [
      {
        name: 'firmware-downloads',
        // The code is optional so that an address with none, such as a link from before codes existed, reaches a
        // page that says what to do instead of a not-found.
        path: 'firmware/downloads/:code?',
        labelKey: 'nav.firmwareDownloads',
        icon: Download,
        hidden: true,
        component: () => import('@/views/firmware/FirmwareDownloadView.vue'),
        // Every package's own permission, plus both release permissions: whoever manages releases can see what a
        // supplier is shown. A new package adds its permission here — the endpoint behind the page already
        // answers only about the packages the caller may have.
        permissions: [
          Permission.FirmwareCcu5Download,
          Permission.FirmwareReleaseRead,
          Permission.FirmwareReleaseWrite
        ]
      },
      {
        name: 'firmware-releases',
        path: 'firmware/releases',
        labelKey: 'nav.firmwareReleases',
        icon: UploadFilled,
        component: () => import('@/views/firmware/FirmwareReleaseListView.vue'),
        permissions: [Permission.FirmwareReleaseRead, Permission.FirmwareReleaseWrite]
      }
    ]
  },
  {
    // Both pages are about a code that ends up on physical hardware. Issuing comes first and looking one up
    // second, because that is the order the work happens in — and the second page is the only place a code
    // that really exists can be found, since this service cannot issue its own.
    labelKey: 'nav.section.codes',
    items: [
      {
        name: 'stock-issuance',
        path: 'stock/issuance',
        labelKey: 'nav.stockIssuance',
        icon: CollectionTag,
        component: () => import('@/views/stock/IssuanceView.vue'),
        // Both, because isAllowed is an OR and every read behind the page accepts either: whoever may issue
        // stock has to see what has already been issued, and whoever only reviews it must not need the
        // permission to add to a pool. stock:code:issue is held by no role in a fresh database.
        permissions: [Permission.StockCodeRead, Permission.StockCodeIssue]
      },
      {
        name: 'legacy-codes',
        path: 'legacy/codes',
        labelKey: 'nav.legacyCodes',
        icon: Postcard,
        component: () => import('@/views/legacy/CodeListView.vue'),
        // Held by no role in a fresh database, so only ADMINISTRATOR reaches it. Nobody on a line needs it: a
        // station is told whether a code is acceptable by the binding check itself.
        permissions: [Permission.LegacyCodeRead]
      }
    ]
  },
  {
    labelKey: 'nav.section.admin',
    items: [
      {
        name: 'organisations',
        path: 'admin/organisations',
        labelKey: 'nav.organisations',
        icon: OfficeBuilding,
        component: () => import('@/views/admin/OrganisationListView.vue'),
        permissions: [Permission.IamOrganisationRead]
      },
      {
        // Before operators: an account is created holding a role, so the roles have to exist first.
        name: 'roles',
        path: 'admin/roles',
        labelKey: 'nav.roles',
        icon: Key,
        component: () => import('@/views/admin/RoleListView.vue'),
        permissions: [Permission.IamRoleRead]
      },
      {
        name: 'operators',
        path: 'admin/operators',
        labelKey: 'nav.operators',
        icon: UserFilled,
        component: () => import('@/views/admin/OperatorListView.vue'),
        permissions: [Permission.IamOperatorRead]
      },
      {
        name: 'chirp-credential',
        path: 'admin/chirp-credential',
        labelKey: 'nav.chirpCredential',
        icon: Connection,
        component: () => import('@/views/admin/ChirpCredentialView.vue'),
        permissions: [Permission.OpsChirpMint]
      }
    ]
  }
]

/** Flattened, for the router. */
export const navItems: NavItem[] = navigation.flatMap((section) => section.items)

export function findNavItem(name: string): NavItem | undefined {
  return navItems.find((item) => item.name === name)
}

/**
 * @param granted the permissions the signed-in operator holds
 * @param required the permissions a page accepts, if it restricts at all
 */
export function isAllowed(granted: string[], required?: PermissionName[]): boolean {
  if (!required || required.length === 0) {
    return true
  }
  if (granted.includes(ALL_PERMISSIONS)) {
    return true
  }
  return required.some((permission) => granted.includes(permission))
}
